/**
 * BarcodeService
 * Service for barcode generation and validation
 * Route: api/codes/barcode
 * Controller: BarcodesController.cs
 */
import { BaseService } from '../base/BaseService';
import { ReturnType, } from '../../models/codegenerators';
/**
 * BarcodeService
 * Handles barcode generation, validation, and decoding operations
 */
export class BarcodeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? BarcodeService.DEFAULT_SERVICE_PATH,
            basePath: BarcodeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== BARCODE GENERATION ====================
    /**
     * Generate a barcode in the specified format
     * POST api/codes/barcode/generate
     */
    async generate(data) {
        const response = await this.generateRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate barcode');
        }
        return response.data;
    }
    /**
     * Generate barcode with full response
     */
    generateRaw(data) {
        const endpoint = this.getEndpoint('generate', 'generate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Generate an EAN-13 barcode (quick endpoint)
     * POST api/codes/barcode/ean13
     */
    async generateEAN13(data, returnType = ReturnType.File) {
        const response = await this.generateEAN13Raw(data, returnType);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate EAN-13 barcode');
        }
        return response.data;
    }
    /**
     * Generate EAN-13 with full response
     */
    generateEAN13Raw(data, returnType = ReturnType.File) {
        const endpoint = this.getEndpoint('ean13', 'ean13');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        formData.append('data', data);
        formData.append('returnType', returnType);
        return this.http.post(url, formData);
    }
    /**
     * Generate a Code 128 barcode (quick endpoint)
     * POST api/codes/barcode/code128
     */
    async generateCode128(data, returnType = ReturnType.File) {
        const response = await this.generateCode128Raw(data, returnType);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate Code 128 barcode');
        }
        return response.data;
    }
    /**
     * Generate Code 128 with full response
     */
    generateCode128Raw(data, returnType = ReturnType.File) {
        const endpoint = this.getEndpoint('code128', 'code128');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        formData.append('data', data);
        formData.append('returnType', returnType);
        return this.http.post(url, formData);
    }
    /**
     * Generate a UPC-A barcode (quick endpoint)
     * POST api/codes/barcode/upc-a
     */
    async generateUPCA(data, returnType = ReturnType.File) {
        const response = await this.generateUPCARaw(data, returnType);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate UPC-A barcode');
        }
        return response.data;
    }
    /**
     * Generate UPC-A with full response
     */
    generateUPCARaw(data, returnType = ReturnType.File) {
        const endpoint = this.getEndpoint('upc-a', 'upc-a');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        formData.append('data', data);
        formData.append('returnType', returnType);
        return this.http.post(url, formData);
    }
    // ==================== VALIDATION ====================
    /**
     * Validate barcode data for a specific format
     * POST api/codes/barcode/validate
     */
    async validateData(data, format) {
        const response = await this.validateDataRaw(data, format);
        return response.data ?? false;
    }
    /**
     * Validate barcode data with full response
     */
    validateDataRaw(data, format) {
        const endpoint = this.getEndpoint('validate', 'validate');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        formData.append('data', data);
        formData.append('format', format);
        return this.http.post(url, formData);
    }
    // ==================== DECODING ====================
    /**
     * Decode a barcode from an uploaded image
     * POST api/codes/barcode/decode
     */
    async decode(imageFile) {
        const response = await this.decodeRaw(imageFile);
        if (!response.data) {
            throw new Error(response.message || 'Failed to decode barcode');
        }
        return response.data;
    }
    /**
     * Decode barcode with full response
     */
    decodeRaw(imageFile) {
        const endpoint = this.getEndpoint('decode', 'decode');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        formData.append('image', imageFile);
        return this.http.post(url, formData);
    }
}
BarcodeService.DEFAULT_SERVICE_PATH = 'codegenerators';
BarcodeService.BASE_PATH = 'api/codes/barcode';
//# sourceMappingURL=BarcodeService.js.map